/** @jsxImportSource @emotion/react */
import React from 'react'
import PropTypes from 'prop-types'
import { Trans, Translation } from 'react-i18next'
import { jsx, css } from '@emotion/react'
import { get } from 'lodash'
import { Row, Col, Modal } from 'react-bootstrap'
import ReactAudioPlayer from 'react-audio-player'
import CurrencyValue from './CurrencyValue'
import { default_theme as theme } from '../emotion/theme'
import { StandaloneCheckboxField } from './form/CheckboxField'
import { InlineInputField } from './form/InlineInputField'
import { FormikInputField } from './form/InputField'
import { Separator } from './layout/Separator'
import { GrayButton } from './layout/GrayButton'
import { MultipleEmailsSupportedInfoWidget } from './MultipleEmailsSupportedInfoWidget'
import { VoiceNumberGreetingSelector } from './VoiceNumberGreetingSelector'
import * as Yup from 'yup'

export const VoiceCallPlanVoiceMailRow = ({
  isEnabled,
  onToggle,
  initialValues,
  onSave,
  greetingSelectorVisible,
  setGreetingSelectorVisible,
  overrideAnsweringMessageList,
  isSaving,
  isToggleSaving,
  subscription,
  customer_phone_number,
}) => {
  const phoneNumberEmailValidationSchema = Yup.object().shape({
    forward_voicemail_to_email: Yup.string()
      .email('Email must be valid')
      .required('Email is required'),
  })
  const toggleDisabled = !get(customer_phone_number, 'call_forwarding_enabled')
  const startInEditingMode =
    get(customer_phone_number, 'id') &&
    !get(initialValues, 'forward_voicemail_to_email')
  const renderGreetingSelector = (formik_props) => {
    return (
      <Modal show={true} size="xl" onHide={() => setGreetingSelectorVisible(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <Trans>Select a greeting</Trans>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <VoiceNumberGreetingSelector
            formik_props={formik_props}
            field_name="answering_message"
            customer_phone_number={customer_phone_number}
            overrideAnsweringMessageList={overrideAnsweringMessageList}
              onSelected={() => setGreetingSelectorVisible(false)}
              answering_message_category="voice_message"
          />
        </Modal.Body>
      </Modal>
    )
  }

  const renderVoiceMailFields = (formik_props) => {
    const answeringMessage = get(formik_props.values, 'answering_message')
    return (
      <div css={row_editing_fields_container_style}>
        <Row>
          <Col css={multivalue_col_style}>
            <Trans>Record voicemail and forward to</Trans>
          </Col>
        </Row>
        <Separator variant="h30" />

        <Translation>
          {(t) => (
            <Row>
              <Col css={multivalue_col_style}>
                <div css={inline_edit_form_label_style}>
                  <Trans>Email address</Trans>
                  &nbsp;
                  <MultipleEmailsSupportedInfoWidget />
                </div>
                <FormikInputField
                  name="forward_voicemail_to_email"
                  formik_props={formik_props}
                  placeholder={t('Enter an email')}
                />
              </Col>
            </Row>
          )}
        </Translation>

        <Separator variant="h20" />

        <Row>
          <Col css={multivalue_col_style}>
            <div css={inline_edit_form_label_style}>
              <Trans>Greeting</Trans>
            </div>

            <div css={selected_greeting_style}>
              <Trans>{get(answeringMessage, 'name', 'Default')}</Trans>
            </div>

            {answeringMessage && (
              <>
                <Separator variant="w20" />
                <ReactAudioPlayer
                  src={get(
                    answeringMessage,
                    'answering_message_file_info_details.original_download_url'
                  )}
                  controls
                />
              </>
            )}

            <Separator variant="w20" />
          </Col>
        </Row>

        <Separator variant="h20" />

        <Row>
          <Col css={multivalue_col_style}>
            <div css={inline_edit_form_label_style}>&nbsp;</div>
            <GrayButton onClick={() => setGreetingSelectorVisible(true)} auto_disable={false}>
              <Trans>Change greeting</Trans>
            </GrayButton>
          </Col>
        </Row>

        {greetingSelectorVisible && renderGreetingSelector(formik_props)}

        <Separator variant="h30" />
      </div>
    )
  }

  return (
    <div css={[row_style, white_row_style, isEnabled ? null : disabled_row_style]}>
      <div css={toggle_column_style}>
        <div css={toggle_checkbox_cell_style}>
          {toggleDisabled && (
            <StandaloneCheckboxField label="" disabled={true} variant="large" checked={isEnabled} />
          )}
          {!toggleDisabled && (
            <StandaloneCheckboxField
              onChange={onToggle}
              label=""
              is_saving={isToggleSaving}
              variant="large"
              checked={isEnabled}
            />
          )}
        </div>
      </div>
      <div css={value_column_style}>
        <InlineInputField
          startInEditingMode={startInEditingMode}
          formButtonStyle="buttons"
          canEdit={isEnabled}
          readOnlyValue={
            <div css={readonly_row_style}>
              <Trans>Record voicemail and forward to</Trans>
              <Separator variant="w10" />
              <div css={phone_number_email_value_style}>
                {get(initialValues, 'forward_voicemail_to_email') || 'Enter an email'}
              </div>
              {get(subscription, 'voicemail_rate_per_minute_excluding_vat_euros') > 0 && (
                <>
                  <Separator variant="w10" />
                  <div css={readonly_number_info_text_style}>
                    <CurrencyValue value={subscription.voicemail_rate_per_minute_excluding_vat_euros} />/<Trans>minute</Trans>
                  </div>
                </>
              )}
              {!get(subscription, 'voicemail_rate_per_minute_excluding_vat_euros') &&
                get(subscription, 'voicemail_start_rate_excluding_vat_euros') > 0 && (
                  <>
                    <Separator variant="w10" />
                    <div css={readonly_number_info_text_style}>
                      <CurrencyValue value={subscription.voicemail_start_rate_excluding_vat_euros} />/<Trans>voicemail</Trans>
                    </div>
                  </>
                )}
            </div>
          }
          formInitialValues={initialValues}
          onSave={onSave}
          is_saving={isSaving}
          validationSchema={phoneNumberEmailValidationSchema}
          renderFormFields={renderVoiceMailFields}
        />
      </div>
    </div>
  )
}

VoiceCallPlanVoiceMailRow.propTypes = {
  isEnabled: PropTypes.any,
  onToggle: PropTypes.any,
  initialValues: PropTypes.any,
  onSave: PropTypes.any,
  greetingSelectorVisible: PropTypes.any,
  setGreetingSelectorVisible: PropTypes.any,
  overrideAnsweringMessageList: PropTypes.any,
  isSaving: PropTypes.any,
  isToggleSaving: PropTypes.any,
  subscription: PropTypes.any,
  customer_phone_number: PropTypes.any,
  phoneNumberEmailValidationSchema: PropTypes.any,
}

const row_style = css`
  display: flex;
  flex-wrap: wrap;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 15px;
  width: 100%;
  margin-left: 0px;
  margin-right: 30px;
  align-items: center;
  min-height: 68px;
`

const white_row_style = css`
  background-color: #fff;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  align-items: center;
  line-height: 18px;
`

const disabled_row_style = css`
  color: ${theme.colors.dark_grey};
`

const toggle_column_style = css`
  display: flex;
  margin-right: 15px;
  min-width: 40px;
  max-width: 40px;
  align-self: flex-start;
  margin-top: 8px;
  justify-content: center;
  width: 100%;
`

const toggle_checkbox_cell_style = css`
  display: flex;
  flex-grow: 1;
`

const value_column_style = css`
  padding-bottom: 15px;
  flex-grow: 1;
`

const multivalue_col_style = css`
  display: flex;
  align-items: center;
  min-width: 150px;
`

const inline_edit_form_label_style = css`
  width: 150px;
`

const readonly_row_style = css`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
`

const phone_number_email_value_style = css`
  font-weight: bold;
`

const selected_greeting_style = css`
  font-weight: bold;
`

const readonly_number_info_text_style = css`
  display: flex;
`

const row_editing_fields_container_style = css`
  margin-top: 10px;
`


